import React, { useEffect } from 'react'
import styled from 'styled-components'
import { useFormContext, Controller, useFieldArray } from 'react-hook-form'
import FocusSkillStarWhite from 'images/focus_skill/focus_skill_star_white.svg'
import FocusSkillStarGreen from 'images/focus_skill/focus_skill_star_green.svg'
import FocusSkillStarGray from 'images/focus_skill/focus_skill_star_gray.svg'
import { SkillListProps } from './types'
import { Select } from 'spa/member/components/Select'
import { UserSkillRelation } from "../../../../../javascripts/api/generated";
import IconError from 'images/form/icon_error.svg'

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 8px;
  border-top: 1px solid ${props => props.theme.members.colors.silver._30};
`

const SkillItemContainer = styled.div`
  border-bottom: 1px dashed ${props => props.theme.members.colors.silver._50};

  &:last-of-type {
    border-bottom: none;
  }

  @media screen and (${props => props.theme.media.sm}) {
    border-bottom: none;
  }
`

const SkillItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 4px;
  gap: 1rem;
`

const SkillInfo = styled.div`
  display: flex;
  width: 100%;
  gap: 1rem;
  flex-direction: column;

  @media screen and (${props => props.theme.media.sm}) {
    gap: .5rem;
    flex-direction: row;
  }
`

const MainSkillInfoContainer = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  padding: 1rem 0 0;

  @media screen and (${props => props.theme.media.sm}) {
    border-bottom: 1px dashed ${props => props.theme.members.colors.silver._50};
    padding-bottom: 1rem;
  }
`

const MainSkillInfo = styled.div`
  display: flex;
  align-items: center;
  flex-grow: 1;
`

const StarLabel = styled.label`
  display: flex;
  align-items: center;
  margin-bottom: 0;
  cursor: pointer;
`

const HiddenCheckbox = styled.input<React.InputHTMLAttributes<HTMLInputElement>>`
  display: none;
`

const SkillName = styled.div`
  flex-grow: 1;
  border-bottom: none;
  font-weight: 700;
  font-size: .875rem;
  display: flex;
  align-items: center;
  color: ${props => props.theme.members.colors.black};
  padding: 0 8px;
`

const SubSkillInfoContainer = styled.div`
  display: flex;
  gap: 1rem;
  padding-bottom: 1rem;

  @media ${props => props.theme.media.sm} {
    padding-bottom: 0;
  }
`

const StyledYearSelectContainer = styled.div`
  width: 100%;

  @media ${props => props.theme.media.sm} {
    width: 10rem;
    align-self: center;
  }
`

const DeleteButtonContainer = styled.div`
  align-self: flex-end;
  padding: 9px 0;

  @media ${props => props.theme.media.sm} {
    align-self: center;
  }
`

const DeleteButton = styled.button`
  padding: 8px 16px;
  width: 100px;
  color: #D52020;
  border: 1px solid #D52020;
  background-color:  ${props => props.theme.members.colors.white};
  border-radius: 40px;
  cursor: pointer;
  font-weight: 700;
  font-size: 12px;
  line-height: 1;
`

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: .5rem;
  color: #F15252;
  font-size: 0.75rem;
  margin-top: .5rem;
  margin-right: .5rem;
  line-height: 1.7;
`

export const StyledSkillList: React.FC<SkillListProps> = props => {
  const { control, getValues, setValue, setError, watch, clearErrors } = useFormContext()

  const { fields, remove } = useFieldArray({
    control,
    name: `categorizedSkills.${props.categoryId}`
  })

  const [focusedSkillsCount, setFocusedSkillsCount] = React.useState(0)

  const watchSkills = watch()

  useEffect(() => {
    const focusedSkills = Object.values(getValues('categorizedSkills') || {}).flatMap((skills: UserSkillRelation[]) => {
      return skills.filter(skill => skill.isFocusSkill)
    })

    setFocusedSkillsCount(focusedSkills.length)
  }, [watchSkills])

  const validateFocusedSkillsCount = (checked: boolean, index: number) => {
    const key = `categorizedSkills.${props.categoryId}.${index}.isFocusSkill`

    if (!checked || focusedSkillsCount < 5) {
      clearErrors(key)
      return
    }

    setValue(key, false)
    setError(key, { message: '注力スキルの選択可能な上限に達しています。' })
  }

  const yearOptions = [
    { value: '', label: '経験年数' },
    { value: 0, label: '1年未満' },
    { value: 1, label: '1年以上' },
    { value: 2, label: '2年以上' },
    { value: 3, label: '3年以上' },
    { value: 4, label: '4年以上' },
    { value: 5, label: '5年以上' },
    { value: 6, label: '6年以上' },
    { value: 7, label: '7年以上' },
    { value: 8, label: '8年以上' },
    { value: 9, label: '9年以上' },
    { value: 10, label: '10年以上' }
  ]

  return(
    <ListWrapper>
      {fields?.map((skill, index) => (
        <SkillItemContainer key={skill.masterSkillId}>
          <SkillItem data-cy="skill-item">
            <SkillInfo>
              <MainSkillInfoContainer>
                <MainSkillInfo>
                  <Controller
                    name={`categorizedSkills.${props.categoryId}.${index}.isFocusSkill`}
                    control={control}
                    render={({ field }) => (
                      <>
                        <StarLabel htmlFor={`is-focus-skill-${skill.masterSkillId}`} >
                          <img
                            src={
                                field.value ? FocusSkillStarGreen
                                  : focusedSkillsCount < 5 ? FocusSkillStarWhite : FocusSkillStarGray
                              }
                            width="20" height="20" alt="注力スキル" />
                        </StarLabel>
                        <HiddenCheckbox
                          type="checkbox" id={`is-focus-skill-${skill.masterSkillId}`} {...field}
                          checked={field.value}
                          onChange={e => {
                            field.onChange(e.target.checked)
                            validateFocusedSkillsCount(e.target.checked, index)
                          }}
                        />
                      </>
                    )}
                  />
                  <SkillName>{skill.name}</SkillName>
                </MainSkillInfo>
                <Controller
                  name={`categorizedSkills.${props.categoryId}.${index}.isFocusSkill`}
                  control={control}
                  render={({ fieldState: { error } }) => {
                    if (!error) {
                      return null
                    }

                    return (
                      <ErrorMessage>
                        <img src={IconError} alt="alert" />
                        {error.message}
                      </ErrorMessage>
                    )
                  }}
                />
              </MainSkillInfoContainer>
              <SubSkillInfoContainer>
                <StyledYearSelectContainer>
                  <Select
                    inputSize="small"
                    name={`categorizedSkills.${props.categoryId}.${index}.year`}
                    options={yearOptions}
                    placeholder='経験年数'
                    />
                </StyledYearSelectContainer>
                <DeleteButtonContainer>
                  <DeleteButton onClick={() => remove(index)} data-cy="delete-skill">
                    削除
                  </DeleteButton>
                </DeleteButtonContainer>
              </SubSkillInfoContainer>
            </SkillInfo>
          </SkillItem>
        </SkillItemContainer>
      ))}
    </ListWrapper>
  )
}
